import {
  BarChartOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { Button, Col, Modal, Row, Space, Tag, Tooltip, Switch } from "antd";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Client } from "../../core/api-client";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import {
  API_REPORT_METADATA,
  API_REPORT_QUERY,
  API_SAVED_REPORTS,
  ROUTE_REPORT_BUILDER,
  ROUTE_REPORTS,
  API_GET_REPORTS_TABLE_PREFERENCES,
  API_SAVE_REPORTS_TABLE_PREFERENCES,
} from "../../shared/routes/private.routes";
import { exportCsv } from "../../shared/utils/export-csv";
import { formatDate, showNotification } from "../../shared/utils/utils";
import "./report-builder.less";

type ReportColumn = {
  key: string;
  label: string;
  type: string;
};

type ReportDataset = {
  key: string;
  label: string;
  columns: ReportColumn[];
};

const DATASET_LABELS: Record<string, string> = {
  staff: "Staff",
  project: "Project",
  milestone: "Milestone",
};

const DATE_FILTER_LABELS: Record<string, string> = {
  doj: "Date Of Joining",
  internStartDate: "Intern Start Date",
  internEndDate: "Intern End Date",
  traineeStartDate: "Trainee Start Date",
  traineeEndDate: "Trainee End Date",
  contractStartDate: "Contract Start Date",
  contractEndDate: "Contract End Date",
  lwd: "LWD",
  probationEndDate: "Probation End Date",
  resignationDate: "Resignation Date",
};
const REPORT_DEFAULT_VISIBLE_COLUMNS = [
  "reportName",
  "dataset",
  "columns",
  "dateFilterField",
  "fromDate",
  "toDate",
  "updatedDate",
];

const ManageReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);
  const [totalRows, setTotalRows] = useState(0);
  const [datasets, setDatasets] = useState<ReportDataset[]>([]);
  const [exportingReportId, setExportingReportId] = useState<string>();
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    ...REPORT_DEFAULT_VISIBLE_COLUMNS,
  ]);

  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [preferencesCanSave, setPreferencesCanSave] = useState(false);

  const skipNextPreferenceSave = useRef(true);
  const fetchReportsTablePreferences = useCallback(async () => {
    try {
      const response = await Client.get(
        API_GET_REPORTS_TABLE_PREFERENCES
      );

      const savedPreferences = response?.data?.data;

      setPreferencesCanSave(true);

      if (Array.isArray(savedPreferences?.visibleColumns)) {
        const validColumns = savedPreferences.visibleColumns.filter(
          (column: unknown): column is string =>
            typeof column === "string" &&
            REPORT_DEFAULT_VISIBLE_COLUMNS.includes(column)
        );

        setVisibleColumns(
          validColumns.length > 0
            ? validColumns
            : [...REPORT_DEFAULT_VISIBLE_COLUMNS]
        );
      } else {
        setVisibleColumns([...REPORT_DEFAULT_VISIBLE_COLUMNS]);
      }
    } catch (error) {
      console.error(
        "Error fetching Reports table preferences:",
        error
      );
    } finally {
      setPreferencesLoaded(true);
    }
  }, []);

  useEffect(() => {
    void fetchReportsTablePreferences();
  }, [fetchReportsTablePreferences]);

  useEffect(() => {
    if (!preferencesLoaded || !preferencesCanSave) {
      return;
    }

    if (skipNextPreferenceSave.current) {
      skipNextPreferenceSave.current = false;
      return;
    }

    const savePreferences = async () => {
      try {
        await Client.put(API_SAVE_REPORTS_TABLE_PREFERENCES, {
          preferences: {
            visibleColumns,
          },
        });
      } catch (error) {
        console.error(
          "Error saving Reports table preferences:",
          error
        );
      }
    };

    void savePreferences();
  }, [preferencesLoaded, preferencesCanSave, visibleColumns]);
  const loadReports = useCallback(
    async (page = currentPage, nextPageSize = pageSize) => {
      setLoading(true);
      try {
        const response = await Client.get(API_SAVED_REPORTS, {
          params: {
            page,
            limit: nextPageSize,
            sort_by: sorter.sort_by || "",
            sort_type: sorter.sort_type || "DESC",
          },
        });
        setReports(response?.data?.data || []);
        setTotalRows(response?.data?.total || 0);
        setCurrentPage(response?.data?.page || page);
        setPageSize(response?.data?.limit || nextPageSize);
      } catch {
        showNotification("Failed to load saved reports", "error");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, sorter.sort_by, sorter.sort_type]
  );

  useEffect(() => {
    loadReports(1, pageSize);
  }, [sorter, pageSize]);

  const loadMetadata = useCallback(async () => {
    try {
      const response = await Client.get(API_REPORT_METADATA);
      setDatasets(response?.data?.datasets || []);
    } catch {
      showNotification("Failed to load report datasets", "error");
    }
  }, []);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const getExportColumns = (report: any) => {
    const dataset = datasets.find((item) => item.key === report.dataset);
    const columnMap = new Map(
      (dataset?.columns || []).map((column) => [column.key, column])
    );

    return (report.columns || []).map((columnKey: string) => {
      return (
        columnMap.get(columnKey) || {
          key: columnKey,
          label: columnKey,
          type: "string",
        }
      );
    });
  };

  const exportSavedReport = async (report: any) => {
    if (!report?.columns?.length) {
      showNotification("No columns selected for this report.", "warning");
      return;
    }

    setExportingReportId(report.reportId);
    try {
      const firstResponse = await Client.post(
        API_REPORT_QUERY,
        {
          dataset: report.dataset,
          columns: report.columns,
          dateFilter: report.dateFilter,
        },
        {
          params: {
            page: 1,
            limit: 1,
          },
        }
      );
      const total = firstResponse?.data?.total || 0;

      if (!total) {
        showNotification("No report data found to export.", "warning");
        return;
      }

      const response = await Client.post(
        API_REPORT_QUERY,
        {
          dataset: report.dataset,
          columns: report.columns,
          dateFilter: report.dateFilter,
        },
        {
          params: {
            page: 1,
            limit: total,
          },
        }
      );
      const exportRows = response?.data?.data || [];

      exportCsv(
        `${report.reportName || report.dataset || "saved"}_report.csv`,
        exportRows,
        getExportColumns(report)
      );
    } catch {
      showNotification("Failed to export report data", "error");
    } finally {
      setExportingReportId(undefined);
    }
  };

  const deleteReport = (report: any) => {
    Modal.confirm({
      title: "Delete report?",
      content: report?.reportName,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          await Client.delete(`${API_SAVED_REPORTS}/${report.reportId}`);
          showNotification("Report deleted successfully", "success");
          loadReports(1, pageSize);
        } catch {
          showNotification("Failed to delete report", "error");
        }
      },
    });
  };

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const columns = useMemo(
    () => [
      {
        title: "Report Name",
        dataIndex: "reportName",
        key: "reportName",
        ...getSortableColumnProps("reportName"),
      },
      {
        title: "Dataset",
        dataIndex: "dataset",
        key: "dataset",
        ...getSortableColumnProps("dataset"),
        render: (value: string) => DATASET_LABELS[value] || value || "-",
      },
      {
        title: "Columns",
        dataIndex: "columns",
        key: "columns",
        render: (value: string[]) => (
          <Tag color="blue">{Array.isArray(value) ? value.length : 0}</Tag>
        ),
      },
      {
        title: "Filter Date By",
        dataIndex: "dateFilter",
        key: "dateFilterField",
        render: (value: any) =>
          value?.field ? DATE_FILTER_LABELS[value.field] || value.field : "-",
      },
      {
        title: "From Date",
        dataIndex: "dateFilter",
        key: "fromDate",
        render: (value: any) => value?.fromDate ? formatDate(value.fromDate) : "-",
      },
      {
        title: "To Date",
        dataIndex: "dateFilter",
        key: "toDate",
        render: (value: any) => value?.toDate ? formatDate(value.toDate) : "-",
      },
      {
        title: "Updated Date",
        dataIndex: "updatedDate",
        key: "updatedDate",
        ...getSortableColumnProps("updatedDate"),
        render: (value: string) => value ? formatDate(value) : "-",
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_: any, record: any) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => navigate(`${ROUTE_REPORTS}/edit/${record.reportId}`)}
              />
            </Tooltip>
            <Tooltip title="Export">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                loading={exportingReportId === record.reportId}
                onClick={() => exportSavedReport(record)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteReport(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [datasets, exportingReportId, navigate, pageSize, sorter.sort_by, sorter.sort_type]
  );
  const displayedColumns = useMemo(
    () =>
      columns.filter(
        (column: any) =>
          column.key === "actions" ||
          visibleColumns.includes(column.key)
      ),
    [columns, visibleColumns]
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <BarChartOutlined />
              </div>
              <h1 className="app-page-title">Manage Reports</h1>
            </div>
            <p className="app-page-subtitle">
              View saved report definitions and create new reports.
            </p>
          </Col>
          <Col>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={() => loadReports(1, pageSize)}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(ROUTE_REPORT_BUILDER)}
              >
                Create New Report
              </Button>
              <Tooltip title="Customize Table">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<TableOutlined />}
                  aria-label="Customize Table"
                  onClick={() => setColumnSettingsOpen(true)}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          pagination
          loading={loading}
          dataSource={reports.map((report) => ({
            ...report,
            key: report.reportId,
          }))}
          columns={displayedColumns}
          defaultPage={1}
          currentPage={currentPage}
          totalRows={totalRows}
          pageSize={pageSize}
          onChange={onChangeHandler}
          onPageChange={(page: number) => loadReports(page, pageSize)}
          onPageSizeChange={(page: number, nextPageSize: number) =>
            loadReports(page, nextPageSize)
          }
        />
      </div>
      <Modal
        title="Customize Table View"
        open={columnSettingsOpen}
        onCancel={() => setColumnSettingsOpen(false)}
        width={560}
        footer={
          <Space>
            <Button
              onClick={() =>
                setVisibleColumns([...REPORT_DEFAULT_VISIBLE_COLUMNS])
              }
            >
              Reset to Default
            </Button>

            <Button
              type="primary"
              onClick={() => setColumnSettingsOpen(false)}
            >
              Done
            </Button>
          </Space>
        }
      >
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Row gutter={[24, 0]} style={{ margin: 0 }}>
            {columns
              .filter((column: any) => column.key !== "actions")
              .map((column: any) => {
                const checked = visibleColumns.includes(column.key);

                return (
                  <Col xs={24} sm={12} key={column.key}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "12px 0",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <span>
                        {typeof column.title === "string"
                          ? column.title
                          : column.key}
                      </span>

                      <Switch
                        size="small"
                        checked={checked}
                        aria-label={String(column.title)}
                        disabled={
                          checked && visibleColumns.length === 1
                        }
                        onChange={(enabled) =>
                          setVisibleColumns((previous) =>
                            enabled
                              ? previous.includes(column.key)
                                ? previous
                                : [...previous, column.key]
                              : previous.filter(
                                (key) => key !== column.key
                              )
                          )
                        }
                      />
                    </div>
                  </Col>
                );
              })}
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default ManageReportsPage;
